<template>
    <section class="ilg">

        <!-- Section card -->
        <div class="ilg-card">

            <!-- Header -->
            <div class="ilg-head">
                <div class="ilg-head-l">
                    <div class="ilg-head-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.22);`">
                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="ilg-head-title">{{ t('investmentHistory') }}</h2>
                        <p class="ilg-head-sub">{{ t('trackInvestmentActivities') }}</p>
                    </div>
                </div>
                <div class="ilg-total-chip" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.2);`">
                    <span class="ilg-total-n" :style="`color:${themeColors.primary};`">{{ logs?.total || filteredLogs.length }}</span>
                    <span class="ilg-total-l">{{ t('totalInvestments') }}</span>
                </div>
            </div>

            <!-- Filter tabs -->
            <div class="ilg-filters" role="tablist">
                <button
                    v-for="filter in filters"
                    :key="filter.value"
                    class="ilg-filter-btn"
                    :class="activeFilter === filter.value ? 'ilg-filter-btn--on' : ''"
                    :style="activeFilter === filter.value
                        ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 14px rgba(${themeColors.primaryRgb},0.35);`
                        : ''"
                    @click="activeFilter = filter.value"
                    role="tab"
                    :aria-selected="activeFilter === filter.value"
                    :aria-label="`${t('filterBy')} ${filter.label}`"
                >{{ filter.label }}</button>
            </div>

            <!-- Empty state -->
            <div v-if="!filteredLogs || filteredLogs.length === 0" class="ilg-empty">
                <div class="ilg-empty-ic" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.18);color:rgba(${themeColors.primaryRgb},0.6);`">
                    <svg width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                </div>
                <p class="ilg-empty-title">{{ t('noInvestmentsFound') }}</p>
            </div>

            <!-- ── MOBILE ROWS (< 1024px) ── -->
            <div v-else class="ilg-mob">
                <div
                    v-for="log in filteredLogs"
                    :key="log.id"
                    class="ilg-row"
                    @click="showInvestmentDetails(log)"
                    role="button"
                    tabindex="0"
                    @keydown.enter="showInvestmentDetails(log)"
                >
                    <!-- Left: color dot + plan info -->
                    <div class="ilg-row-l">
                        <span class="ilg-row-dot" :style="`background:${getStatusBadgeColor(log.status)};box-shadow:0 0 8px ${getStatusBadgeColor(log.status)}60;`"></span>
                        <div class="ilg-row-info">
                            <p class="ilg-row-plan">{{ log.plan_name }}</p>
                            <p class="ilg-row-date">{{ formatDate(log.started_at) }} · #{{ log.investment_id }}</p>
                        </div>
                    </div>

                    <!-- Right: earned + status -->
                    <div class="ilg-row-r">
                        <p class="ilg-row-earned" :style="`color:${themeColors.primary};`">
                            +{{ currencySymbol }}{{ formatNumber(log.earned_amount) }}
                        </p>
                        <span class="ilg-row-badge" :style="`background:${getStatusBadgeColor(log.status)}1a;border-color:${getStatusBadgeColor(log.status)}35;color:${getStatusBadgeColor(log.status)};`">
                            {{ formatStatus(log.status) }}
                        </span>
                    </div>

                    <!-- Progress strip -->
                    <div class="ilg-row-prog">
                        <div class="ilg-row-prog-bar">
                            <div class="ilg-row-prog-fill"
                                 :style="`width:${calculateProgress(log)}%;background:linear-gradient(90deg,#7c3aed,${themeColors.primary});`">
                            </div>
                        </div>
                        <div class="ilg-row-prog-meta">
                            <span class="ilg-row-prog-txt">
                                <span class="block">{{ currencySymbol }}{{ formatNumber(log.amount) }} invested</span>
                                <span v-if="log.btc_amount" class="text-xs text-amber-400/70 font-mono">₿{{ formatBtcAmount(log.btc_amount) }}</span>
                            </span>
                            <span class="ilg-row-prog-pct" :style="`color:${themeColors.primary};`">{{ calculateProgress(log) }}%</span>
                        </div>
                    </div>

                    <!-- Profit breakdown (mobile) -->
                    <div v-if="log.profit_progress" class="ilg-row-ppb" @click.stop>
                        <ProfitProgressBar
                            :progress-data="log.profit_progress"
                            :show-reinvest-button="false"
                            :primary-color="themeColors.primary"
                        />
                    </div>
                </div>
            </div>

            <!-- ── DESKTOP TABLE (≥ 1024px) ── -->
            <div v-if="filteredLogs && filteredLogs.length > 0" class="ilg-tbl-wrap">
                <table class="ilg-tbl" role="table">
                    <thead>
                        <tr class="ilg-tbl-hdr">
                            <th class="ilg-th">{{ t('plan') }}</th>
                            <th class="ilg-th">{{ t('invested') }}</th>
                            <th class="ilg-th">{{ t('expected') }}</th>
                            <th class="ilg-th">{{ t('earned') }}</th>
                            <th class="ilg-th">{{ t('returns') }}</th>
                            <th class="ilg-th">{{ t('progress') }}</th>
                            <th class="ilg-th">{{ t('status') }}</th>
                            <th class="ilg-th">{{ t('date') }}</th>
                            <th class="ilg-th"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="log in filteredLogs" :key="log.id">
                            <tr class="ilg-tr" @click="showInvestmentDetails(log)">
                                <td class="ilg-td">
                                    <div class="ilg-td-plan">
                                        <span class="ilg-td-dot" :style="`background:${getStatusBadgeColor(log.status)};`"></span>
                                        <div>
                                            <p class="ilg-td-plan-name">{{ log.plan_name }}</p>
                                            <p class="ilg-td-plan-id">#{{ log.investment_id }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td class="ilg-td">
                                    <div>
                                        <span class="ilg-td-val block">{{ currencySymbol }}{{ formatNumber(log.amount) }}</span>
                                        <span v-if="log.btc_amount" class="text-xs text-amber-400/70 font-mono mt-1">₿{{ formatBtcAmount(log.btc_amount) }}</span>
                                    </div>
                                </td>
                                <td class="ilg-td">
                                    <span class="ilg-td-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(log.expected_return) }}</span>
                                </td>
                                <td class="ilg-td">
                                    <span class="ilg-td-val" style="color:#10b981;">{{ currencySymbol }}{{ formatNumber(log.earned_amount) }}</span>
                                </td>
                                <td class="ilg-td">
                                    <span class="ilg-td-returns">{{ log.returns_paid }}<span class="ilg-td-returns-of">/{{ log.total_returns_expected }}</span></span>
                                </td>
                                <td class="ilg-td">
                                    <div class="ilg-td-prog">
                                        <div class="ilg-td-prog-track">
                                            <div class="ilg-td-prog-fill" :style="`width:${calculateProgress(log)}%;background:linear-gradient(90deg,#7c3aed,${themeColors.primary});`"></div>
                                        </div>
                                        <span class="ilg-td-prog-pct" :style="`color:${themeColors.primary};`">{{ calculateProgress(log) }}%</span>
                                    </div>
                                </td>
                                <td class="ilg-td">
                                    <span class="ilg-status-badge" :style="`background:${getStatusBadgeColor(log.status)}1a;border-color:${getStatusBadgeColor(log.status)}35;color:${getStatusBadgeColor(log.status)};`">
                                        <i class="ilg-status-dot" :style="`background:${getStatusBadgeColor(log.status)};`"></i>
                                        {{ formatStatus(log.status) }}
                                    </span>
                                </td>
                                <td class="ilg-td">
                                    <p class="ilg-td-date">{{ formatDate(log.started_at) }}</p>
                                    <p class="ilg-td-time">{{ formatTime(log.started_at) }}</p>
                                </td>
                                <td class="ilg-td">
                                    <button class="ilg-view-btn"
                                            :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`"
                                            @click.stop="showInvestmentDetails(log)">
                                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                                        </svg>
                                    </button>
                                </td>
                            </tr>
                            <!-- Profit cap sub-row -->
                            <tr v-if="log.profit_progress" class="ilg-subrow">
                                <td colspan="9" class="ilg-subrow-td">
                                    <ProfitProgressBar
                                        :progress-data="log.profit_progress"
                                        :show-reinvest-button="false"
                                        :primary-color="themeColors.primary"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="logs?.data && logs.data.length > 0 && logs.links" class="ilg-pagi">
                <span class="ilg-pagi-info">
                    {{ t('showingResults', {from: logs.from||0, to: logs.to||0, total: logs.total||0}) }}
                </span>
                <div class="ilg-pagi-btns">
                    <button
                        v-for="(link, i) in logs.links"
                        :key="i"
                        :disabled="!link.url"
                        class="ilg-pagi-btn"
                        :class="link.active ? 'ilg-pagi-btn--on' : !link.url ? 'ilg-pagi-btn--off' : ''"
                        :style="link.active ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 12px rgba(${themeColors.primaryRgb},0.3);` : ''"
                        @click="goToPage(link.url)"
                        v-html="link.label"
                    />
                </div>
            </div>
        </div>

        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="showModal"
                 class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                 @click.self="closeModal">
                <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                >
                    <div v-if="showModal"
                         class="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                         role="dialog" aria-modal="true">
                        <div
                            class="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 border-b border-slate-700/50 sticky top-0 bg-gradient-to-br from-slate-900/98 to-slate-800/98 backdrop-blur-xl z-10">
                            <div class="flex items-center justify-between gap-4">
                                <h3 class="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                                    {{ t('investmentDetails') }}</h3>
                                <button
                                    class="p-2 sm:p-2.5 transition-all duration-200 text-slate-300 hover:text-white rounded-lg hover:bg-slate-700/80 focus:outline-none focus:ring-2 focus:ring-white/20 flex-shrink-0"
                                    @click="closeModal"
                                    aria-label="Close modal"
                                >
                                    <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24" stroke-width="2.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div v-if="selectedInvestment" class="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
                            <!-- Investment Header Card -->
                            <div class="rounded-xl p-5 sm:p-6 lg:p-7 border bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/50 shadow-lg">
                                <div class="flex items-start justify-between gap-4 mb-5">
                                    <div class="flex-1">
                                        <div class="flex items-center gap-3 mb-2">
                                            <div class="w-10 h-10 rounded-lg flex items-center justify-center" :style="{ backgroundColor: themeColors.primary + '20' }">
                                                <svg class="w-6 h-6" :style="{ color: themeColors.primary }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                                                </svg>
                                            </div>
                                            <div>
                                                <h4 class="text-lg sm:text-xl font-bold text-white">{{ selectedInvestment.plan_name }}</h4>
                                                <p class="text-xs sm:text-sm text-slate-400 font-medium">{{ t('investmentPlan') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        class="inline-flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-bold rounded-lg border shadow-sm"
                                        :style="{ backgroundColor: getStatusBadgeColor(selectedInvestment.status) + '20', borderColor: getStatusBadgeColor(selectedInvestment.status) + '40', color: getStatusBadgeColor(selectedInvestment.status) }">
                                        <span class="w-2 h-2 rounded-full animate-pulse" :style="{ backgroundColor: getStatusBadgeColor(selectedInvestment.status) }"></span>
                                        {{ formatStatus(selectedInvestment.status) }}
                                    </span>
                                </div>
                                <div class="font-mono text-sm sm:text-base font-semibold break-all bg-slate-900/50 rounded-lg px-4 py-3 border border-slate-700/30"
                                     :style="{ color: themeColors.primary }">
                                    ID: {{ selectedInvestment.investment_id }}
                                </div>
                            </div>

                            <div class="rounded-xl p-4 sm:p-5 lg:p-6 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 flex items-center text-white">
                                    <div class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                                         :style="{ backgroundColor: themeColors.primary }"></div>
                                    📊 {{ t('investmentInformation') }}
                                </h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                    <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
                                        <div class="flex items-center gap-2 mb-2">
                                            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                            </svg>
                                            <label class="text-xs sm:text-sm text-slate-400 font-medium">{{ t('investmentId') }}</label>
                                        </div>
                                        <p class="font-mono text-sm font-semibold text-white truncate">{{
                                                selectedInvestment.investment_id
                                            }}</p>
                                    </div>
                                    <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
                                        <div class="flex items-center gap-2 mb-2">
                                            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                            </svg>
                                            <label class="text-xs sm:text-sm text-slate-400 font-medium">{{ t('status') }}</label>
                                        </div>
                                        <span
                                            class="inline-block px-3 py-1.5 text-xs font-semibold rounded-full border"
                                            :style="{ backgroundColor: getStatusBadgeColor(selectedInvestment.status) + '20', borderColor: getStatusBadgeColor(selectedInvestment.status) + '30', color: getStatusBadgeColor(selectedInvestment.status) }">
                                            {{ formatStatus(selectedInvestment.status) }}
                                        </span>
                                    </div>
                                    <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
                                        <div class="flex items-center gap-2 mb-2">
                                            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                            </svg>
                                            <label class="text-xs sm:text-sm text-slate-400 font-medium">{{ t('progress') }}</label>
                                        </div>
                                        <p class="font-semibold text-sm sm:text-base text-white">{{
                                                selectedInvestment.returns_paid
                                            }} / {{ selectedInvestment.total_returns_expected }} {{ t('returns') }}</p>
                                    </div>
                                    <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
                                        <div class="flex items-center gap-2 mb-2">
                                            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                            </svg>
                                            <label class="text-xs sm:text-sm text-slate-400 font-medium">{{ t('completion') }}</label>
                                        </div>
                                        <p class="font-bold text-base sm:text-lg" :style="{ color: themeColors.primary }">
                                            {{ calculateProgress(selectedInvestment) }}%
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="rounded-xl p-4 sm:p-5 lg:p-6 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 flex items-center text-white">
                                    <div class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                                         :style="{ backgroundColor: themeColors.primary }"></div>
                                    💰 {{ t('amountDetails') }}
                                </h4>
                                <div class="space-y-3 sm:space-y-4">
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-slate-400 font-medium text-sm sm:text-base">{{
                                                t('investedAmount')
                                            }}:</span>
                                        <span class="font-semibold text-base sm:text-lg text-white">{{
                                                currencySymbol
                                            }}{{ formatNumber(selectedInvestment.amount) }}</span>
                                    </div>
                                    
                                    <!-- BTC Amount Row -->
                                    <div v-if="selectedInvestment.btc_amount" class="flex justify-between items-center gap-4 p-3 rounded-lg bg-slate-900/40 border border-slate-700/30">
                                        <div class="flex-1">
                                            <span class="text-slate-400 font-medium text-sm sm:text-base flex items-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                                </svg>
                                                BTC Equivalent
                                            </span>
                                        </div>
                                        <div class="text-right">
                                            <div class="font-mono font-semibold text-base sm:text-lg text-amber-400">
                                                ₿{{ formatBtcAmount(selectedInvestment.btc_amount) }}
                                            </div>
                                            <div v-if="selectedInvestment.btc_price_usd" class="text-xs text-slate-500 font-medium mt-1">
                                                @ {{ currencySymbol }}{{ formatNumber(selectedInvestment.btc_price_usd) }}/BTC
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-slate-400 font-medium text-sm sm:text-base">{{
                                                t('expectedReturn')
                                            }}:</span>
                                        <span class="font-semibold text-base sm:text-lg"
                                              :style="{ color: themeColors.primary }">{{
                                                currencySymbol
                                            }}{{ formatNumber(selectedInvestment.expected_return) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-slate-400 font-medium text-sm sm:text-base">{{
                                                t('earnedToDate')
                                            }}:</span>
                                        <span class="font-semibold text-base sm:text-lg"
                                              :style="{ color: themeColors.primaryLight }">{{
                                                currencySymbol
                                            }}{{ formatNumber(selectedInvestment.earned_amount) }}</span>
                                    </div>
                                    
                                    <!-- Profit Breakdown (if available) -->
                                    <div v-if="selectedInvestment.profit_progress" class="pt-3 border-t border-slate-700/50 space-y-2">
                                        <div class="flex justify-between items-center gap-4 text-xs sm:text-sm">
                                            <span class="text-slate-400 font-medium flex items-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                                </svg>
                                                ROI Earned:
                                            </span>
                                            <span class="font-semibold text-green-400">{{ formatProfitValue(selectedInvestment.profit_progress.roi_earned, selectedInvestment.profit_progress.roi_earned_btc_raw || selectedInvestment.profit_progress.roi_earned_btc) }}</span>
                                        </div>
                                        <div class="flex justify-between items-center gap-4 text-xs sm:text-sm">
                                            <span class="text-slate-400 font-medium flex items-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                                </svg>
                                                Referral Commissions:
                                            </span>
                                            <span class="font-semibold text-blue-400">{{ formatProfitValue(selectedInvestment.profit_progress.referral_commissions_earned, selectedInvestment.profit_progress.referral_commissions_earned_btc_raw || selectedInvestment.profit_progress.referral_commissions_earned_btc) }}</span>
                                        </div>
                                    </div>
                                    
                                    <div
                                        class="flex justify-between items-center border-t pt-3 sm:pt-4 border-slate-700/50 gap-4">
                                        <span class="font-semibold text-sm sm:text-base text-white">{{
                                                t('totalPotential')
                                            }}:</span>
                                        <span class="font-bold text-lg sm:text-xl lg:text-2xl"
                                              :style="{ color: themeColors.primary }">{{
                                                currencySymbol
                                            }}{{
                                                formatNumber(parseFloat(selectedInvestment.amount) + parseFloat(selectedInvestment.expected_return))
                                            }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Profit Cap Progress (if available) -->
                            <div v-if="selectedInvestment.profit_progress" class="rounded-xl p-4 sm:p-5 lg:p-6 border bg-gradient-to-br from-slate-800/70 to-slate-900/70 border-slate-700/50">
                                <h4 class="font-bold text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 flex items-center text-white">
                                    <div class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                                         :style="{ backgroundColor: themeColors.primary }"></div>
                                    🎯 {{ t('profitCapProgress') }}
                                </h4>
                                <ProfitProgressBar
                                    :progress-data="selectedInvestment.profit_progress"
                                    :show-reinvest-button="true"
                                    :primary-color="themeColors.primary"
                                    @reinvest="closeModal"
                                />
                            </div>

                            <div class="rounded-xl p-4 sm:p-5 lg:p-6 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 flex items-center text-white">
                                    <div class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                                         :style="{ backgroundColor: themeColors.primary }"></div>
                                    {{ t('investmentProgress') }}
                                </h4>
                                <div class="space-y-3 sm:space-y-4">
                                    <div class="flex justify-between text-xs sm:text-sm mb-3">
                                        <span class="text-slate-300 font-semibold">{{ t('completion') }}</span>
                                        <span class="text-white font-semibold">{{
                                                calculateProgress(selectedInvestment)
                                            }}%</span>
                                    </div>
                                    <div
                                        class="relative w-full bg-slate-700/50 rounded-full h-3 sm:h-4 overflow-hidden">
                                        <div class="h-full rounded-full transition-all duration-1000"
                                             :style="`width: ${calculateProgress(selectedInvestment)}%; background: linear-gradient(to right, ${themeColors.primary}, ${themeColors.primaryLight});`"></div>
                                    </div>
                                    <div class="grid grid-cols-2 gap-3 sm:gap-4 mt-4">
                                        <div class="bg-slate-800/40 rounded-lg p-3 sm:p-4">
                                            <div class="text-xs font-medium text-slate-400 mb-1">{{
                                                    t('returnsPaid')
                                                }}
                                            </div>
                                            <div class="text-lg sm:text-xl font-bold text-white">
                                                {{ selectedInvestment.returns_paid }}
                                            </div>
                                        </div>
                                        <div class="bg-slate-800/40 rounded-lg p-3 sm:p-4">
                                            <div class="text-xs font-medium text-slate-400 mb-1">
                                                {{ t('returnsRemaining') }}
                                            </div>
                                            <div class="text-lg sm:text-xl font-bold"
                                                 :style="{ color: themeColors.primary }">{{
                                                    selectedInvestment.total_returns_expected - selectedInvestment.returns_paid
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="rounded-xl p-4 sm:p-5 lg:p-6 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 flex items-center text-white">
                                    <div class="w-2 h-2 rounded-full mr-3 flex-shrink-0"
                                         :style="{ backgroundColor: themeColors.primary }"></div>
                                    ⏰ {{ t('timeline') }}
                                </h4>
                                <div class="space-y-4">
                                    <div class="flex items-start gap-4 p-3 bg-slate-900/40 rounded-lg border border-slate-700/30">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-green-500/20 border border-green-500/30">
                                            <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <span class="text-xs sm:text-sm text-slate-400 font-medium block mb-1">{{ t('started') }}</span>
                                            <span class="font-semibold text-sm sm:text-base text-white">{{ formatDateTime(selectedInvestment.started_at) }}</span>
                                        </div>
                                    </div>
                                    
                                    <div v-if="selectedInvestment.next_return_at" class="flex items-start gap-4 p-3 rounded-lg border" :style="{ backgroundColor: themeColors.primary + '10', borderColor: themeColors.primary + '30' }">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" :style="{ backgroundColor: themeColors.primary + '20', borderWidth: '2px', borderColor: themeColors.primary + '40' }">
                                            <svg class="w-5 h-5" :style="{ color: themeColors.primary }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <span class="text-xs sm:text-sm text-slate-400 font-medium block mb-1">{{ t('nextReturn') }}</span>
                                            <span class="font-semibold text-sm sm:text-base" :style="{ color: themeColors.primary }">{{ formatDateTime(selectedInvestment.next_return_at) }}</span>
                                        </div>
                                    </div>
                                    
                                    <div v-if="selectedInvestment.maturity_date" class="flex items-start gap-4 p-3 bg-slate-900/40 rounded-lg border border-slate-700/30">
                                        <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-500/20 border border-purple-500/30">
                                            <svg class="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <span class="text-xs sm:text-sm text-slate-400 font-medium block mb-1">{{ t('maturityDate') }}</span>
                                            <span class="font-semibold text-sm sm:text-base text-white">{{ formatDateTime(selectedInvestment.maturity_date) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex justify-end p-4 sm:p-6 lg:p-8 border-t border-slate-700/50 bg-slate-900/50 sticky bottom-0 backdrop-blur-xl">
                            <button
                                class="py-2.5 sm:py-3 px-5 sm:px-6 rounded-xl font-semibold text-sm sm:text-base transition-all duration-200 hover:scale-105 bg-slate-800/80 hover:bg-slate-700/80 text-white focus:outline-none focus:ring-focus:ring-2 focus:ring-white/20"
                                @click="closeModal">
                                {{ t('close') }}
                            </button>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </section>
</template>

<script setup>
import {computed, ref, onMounted, onUnmounted} from 'vue'
import {usePage, router} from '@inertiajs/vue3'
import {useTranslation} from '@/composables/useTranslation'
import {useToast} from '@/composables/useToast'
import ProfitProgressBar from './ProfitProgressBar.vue'

const props = defineProps({
    logs: {
        type: [Array, Object],
        default: () => []
    }
})

defineEmits(['viewDetails', 'viewHistory'])
const page = usePage()
const {t} = useTranslation()
const {showToast} = useToast()
const activeFilter = ref('all')
const showModal = ref(false)
const selectedInvestment = ref(null)

const primaryColor = computed(() => page.props.primaryColor || '#86efac')
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172}
}

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    const newR = Math.min(255, Math.max(0, rgb.r + amount))
    const newG = Math.min(255, Math.max(0, rgb.g + amount))
    const newB = Math.min(255, Math.max(0, rgb.b + amount))
    return rgbToHex(newR, newG, newB)
}

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}))

const currencySymbol = computed(() => page.props.currencySymbol || '$')
const filters = computed(() => [
    {label: t('all'), value: 'all'},
    {label: t('active'), value: 'active'},
    {label: t('completed'), value: 'completed'},
    {label: t('pending'), value: 'pending'},
    {label: t('cancelled'), value: 'cancelled'}
])

const logsData = computed(() => {
    if (props.logs && typeof props.logs === 'object' && props.logs.data) {
        return props.logs.data
    }
    return Array.isArray(props.logs) ? props.logs : []
})

const filteredLogs = computed(() => {
    if (activeFilter.value === 'all') {
        return logsData.value
    }
    return logsData.value.filter(log => log.status === activeFilter.value)
})

const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '0.00'
    const number = parseFloat(num)
    if (isNaN(number)) return '0.00'
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(number)
}

const formatBtcAmount = (num, precision = 12) => {
    if (num === null || num === undefined || num === '') return '0.000000000000'
    const number = parseFloat(num)
    if (isNaN(number)) return '0.000000000000'
    // BTC values in Rio investment views now use 12 decimal places.
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
    }).format(number)
}

const formatProfitValue = (usdValue, btcValue) => {
    if (selectedInvestment.value?.btc_amount && selectedInvestment.value?.btc_price_usd && btcValue !== null && btcValue !== undefined) {
        return `₿${formatBtcAmount(btcValue, 12)}`
    }

    return `${currencySymbol}${formatNumber(usdValue)}`
}

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable')
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return t('invalidDate')
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
    } catch (error) {
        return t('invalidDate')
    }
}

const formatTime = (dateString) => {
    if (!dateString) return ''
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        })
    } catch (error) {
        return ''
    }
}

const formatDateTime = (dateString) => {
    if (!dateString) return t('notAvailable')
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return t('invalidDate')
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    } catch (error) {
        return t('invalidDate')
    }
}

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown')
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const calculateProgress = (log) => {
    if (!log.total_returns_expected || log.total_returns_expected === 0) return 0
    return Math.min(Math.round((log.returns_paid / log.total_returns_expected) * 100), 100)
}

const getStatusBadgeColor = (status) => {
    if (!status || typeof status !== 'string') return '#94a3b8'
    switch (status.toLowerCase()) {
        case 'active':
            return themeColors.value.primary
        case 'completed':
            return '#3b82f6'
        case 'pending':
            return '#fbbf24'
        case 'cancelled':
            return '#ef4444'
        default:
            return '#94a3b8'
    }
}

const showInvestmentDetails = (investment) => {
    if (!investment?.id) {
        showToast(t('invalidInvestmentSelected'), 'error')
        return
    }
    selectedInvestment.value = investment
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
    setTimeout(() => {
        selectedInvestment.value = null
    }, 300)
}

const goToPage = (url) => {
    if (!url || typeof url !== 'string') return
    router.get(url, {}, {
        preserveState: true,
        preserveScroll: true,
        onError: () => showToast(t('failedToLoadPage'), 'error')
    })
}

const handleKeydown = (event) => {
    if (event.key === 'Escape' && showModal.value) {
        closeModal()
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* ── Section wrapper ── */
.ilg {
    margin-top: 32px;
}

/* ── Main card ── */
.ilg-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,0.45);
    position: relative;
}
.ilg-card::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.25), transparent);
    pointer-events: none;
}

/* ── Header ── */
.ilg-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-wrap: wrap;
}
.ilg-head-l {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}
.ilg-head-icon {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.ilg-head-title {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 800;
    color: #fff;
    line-height: 1.2;
    margin: 0 0 3px;
}
.ilg-head-sub {
    font-size: 11.5px;
    color: rgba(148,163,184,0.65);
    margin: 0;
    line-height: 1;
}
.ilg-total-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    border-radius: 50px;
    border: 1px solid;
    flex-shrink: 0;
}
.ilg-total-n {
    font-size: 15px;
    font-weight: 800;
    line-height: 1;
}
.ilg-total-l {
    font-size: 11px;
    color: rgba(148,163,184,0.6);
    font-weight: 600;
    white-space: nowrap;
}

/* ── Filter tabs ── */
.ilg-filters {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 14px 20px;
    overflow-x: auto;
    scrollbar-width: none;
    border-bottom: 1px solid rgba(255,255,255,0.05);
}
.ilg-filters::-webkit-scrollbar { display: none; }

.ilg-filter-btn {
    padding: 6px 16px;
    border-radius: 50px;
    border: 1px solid rgba(255,255,255,0.1);
    background: transparent;
    color: rgba(148,163,184,0.65);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    flex-shrink: 0;
}
.ilg-filter-btn:hover:not(.ilg-filter-btn--on) {
    color: rgba(255,255,255,0.85);
    border-color: rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.05);
}
.ilg-filter-btn--on {
    border-color: transparent;
    font-weight: 800;
}

/* ── Empty state ── */
.ilg-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 56px 24px;
    text-align: center;
}
.ilg-empty-ic {
    width: 68px;
    height: 68px;
    border-radius: 18px;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
}
.ilg-empty-title {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    margin: 0;
}
.ilg-empty-desc {
    font-size: 12.5px;
    color: rgba(148,163,184,0.55);
    margin: 0;
}

/* ── Mobile rows (hidden on desktop) ── */
.ilg-mob {
    display: flex;
    flex-direction: column;
    gap: 0;
}
@media (min-width: 1024px) {
    .ilg-mob { display: none !important; }
}
.ilg-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto auto;
    gap: 6px 12px;
    padding: 13px 18px 13px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: background 0.18s, border-left-color 0.18s;
    align-items: center;
}
.ilg-row:hover { border-left-color: rgba(167,93,237,0.5); }
.ilg-row:last-child { border-bottom: none; }
.ilg-row:hover { background: rgba(167,93,237,0.04); }
.ilg-row:focus { outline: none; background: rgba(167,93,237,0.05); }

.ilg-row-l {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    grid-column: 1;
    grid-row: 1;
}
.ilg-row-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
}
.ilg-row-info { min-width: 0; }
.ilg-row-plan {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.ilg-row-date {
    font-size: 11px;
    color: rgba(148,163,184,0.55);
    margin: 0;
}
.ilg-row-r {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    grid-column: 2;
    grid-row: 1;
}
.ilg-row-earned {
    font-size: 13px;
    font-weight: 800;
    margin: 0;
    line-height: 1;
}
.ilg-row-badge {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 50px;
    border: 1px solid;
    letter-spacing: 0.02em;
}

/* Progress strip */
.ilg-row-prog {
    grid-column: 1 / -1;
    grid-row: 2;
}
.ilg-row-prog-bar {
    width: 100%;
    height: 3px;
    border-radius: 4px;
    background: rgba(255,255,255,0.07);
    overflow: hidden;
    margin-bottom: 4px;
}
.ilg-row-prog-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.6s ease;
}
.ilg-row-prog-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.ilg-row-prog-txt {
    font-size: 10px;
    color: rgba(148,163,184,0.5);
}
.ilg-row-prog-pct {
    font-size: 10px;
    font-weight: 700;
}

/* Profit breakdown block inside mobile row */
.ilg-row-ppb {
    grid-column: 1 / -1;
    grid-row: 3;
    margin-top: 4px;
    padding: 12px;
    background: rgba(124,58,237,0.04);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 10px;
}

/* ── Desktop table (hidden on mobile) ── */
.ilg-tbl-wrap {
    display: none;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.1) transparent;
}
.ilg-tbl-wrap::-webkit-scrollbar { height: 4px; }
.ilg-tbl-wrap::-webkit-scrollbar-track { background: transparent; }
.ilg-tbl-wrap::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

@media (min-width: 1024px) {
    .ilg-tbl-wrap { display: block; }
}

.ilg-tbl {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
}

.ilg-tbl-hdr { border-bottom: 1px solid rgba(255,255,255,0.06); }
.ilg-th {
    padding: 11px 16px;
    text-align: left;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: rgba(148,163,184,0.5);
    white-space: nowrap;
}

.ilg-tr {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    cursor: pointer;
    transition: background 0.15s;
}
.ilg-tr:last-child { border-bottom: none; }
.ilg-tr:hover { background: rgba(167,93,237,0.04); }

.ilg-td {
    padding: 13px 16px;
    vertical-align: middle;
}

.ilg-td-plan {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 160px;
}
.ilg-td-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}
.ilg-td-plan-name {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 2px;
    white-space: nowrap;
}
.ilg-td-plan-id {
    font-size: 10.5px;
    color: rgba(148,163,184,0.45);
    margin: 0;
    font-family: monospace;
}
.ilg-td-val {
    font-size: 13px;
    font-weight: 700;
    color: rgba(226,232,240,0.9);
    white-space: nowrap;
}
.ilg-td-returns {
    font-size: 13px;
    font-weight: 800;
    color: rgba(226,232,240,0.9);
    white-space: nowrap;
}
.ilg-td-returns-of {
    font-size: 11px;
    font-weight: 500;
    color: rgba(148,163,184,0.45);
}

.ilg-td-prog {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
}
.ilg-td-prog-track {
    flex: 1;
    height: 5px;
    border-radius: 5px;
    background: rgba(255,255,255,0.08);
    overflow: hidden;
}
.ilg-td-prog-fill {
    height: 100%;
    border-radius: 5px;
    transition: width 0.7s ease;
}
.ilg-td-prog-pct {
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
}

.ilg-td-date {
    font-size: 12px;
    font-weight: 600;
    color: rgba(226,232,240,0.75);
    margin: 0 0 2px;
    white-space: nowrap;
}
.ilg-td-time {
    font-size: 10.5px;
    color: rgba(148,163,184,0.45);
    margin: 0;
}

.ilg-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 50px;
    border: 1px solid;
    white-space: nowrap;
    letter-spacing: 0.02em;
}
.ilg-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
    animation: ilgDotBlink 2s ease-in-out infinite;
}
@keyframes ilgDotBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
}

.ilg-view-btn {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    cursor: pointer;
    transition: all 0.18s;
    flex-shrink: 0;
}
.ilg-view-btn:hover {
    transform: scale(1.1);
    filter: brightness(1.2);
}

/* Sub-row for profit progress */
.ilg-subrow-td {
    padding: 0 20px 16px 44px;
    background: rgba(124,58,237,0.03);
    border-bottom: 1px solid rgba(255,255,255,0.04);
}

/* ── Pagination ── */
.ilg-pagi {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
}
.ilg-pagi-info {
    font-size: 11.5px;
    color: rgba(148,163,184,0.55);
    font-weight: 500;
}
.ilg-pagi-btns {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}
.ilg-pagi-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.04);
    color: rgba(148,163,184,0.7);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.ilg-pagi-btn:hover:not(.ilg-pagi-btn--on):not(.ilg-pagi-btn--off) {
    background: rgba(255,255,255,0.08);
    color: #fff;
    border-color: rgba(255,255,255,0.2);
}
.ilg-pagi-btn--on {
    border-color: transparent;
    font-weight: 800;
    color: #0f172a;
}
.ilg-pagi-btn--off {
    opacity: 0.3;
    cursor: not-allowed;
}

/* ── Focus reset ── */
button:focus-visible {
    outline: 2px solid rgba(255,255,255,0.25);
    outline-offset: 2px;
}

@media (max-width: 480px) {
    .ilg-head { padding: 14px 14px 12px; }
    .ilg-total-chip { display: none; }
    .ilg-filters { padding: 10px 12px; gap: 5px; }
    .ilg-row { padding: 12px 14px; }
    .ilg-pagi { padding: 12px 14px; flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
